/**
 * One owned component per data stratum (strict grammar, ruled
 * 2026-08-06): a stratum keeps its identity across every projection
 * the way an Element keeps its identity across surfaces. Screens are
 * FORBIDDEN (by lint) from rendering a stratum any other way -- the
 * typed-layers preference applied to pixels.
 */
import type { ReactNode } from 'react';
import { INK } from '../theme';
import { StrataChip } from './chip';
import { ABSENCE, CATEGORY, CATEGORY_DEFAULT, VERDICT, type VerdictState } from './paints';

/** Compliance fact: the binary verdict. Violating red belongs to
 * this component and the canvas's redline geography -- nothing else. */
export function ComplianceTag({
  state,
  children,
}: {
  state: VerdictState;
  children?: ReactNode;
}) {
  return <StrataChip paint={VERDICT[state]}>{children ?? state}</StrataChip>;
}

/** Ruleset row: the provenance pill. The tier is the first token of
 * the provenance string; the tooltip carries the raw citation. */
export function ProvenancePill({ provenance }: { provenance: string }) {
  const tier = provenance.startsWith('retail')
    ? { label: 'retail-cited', paint: VERDICT.complying }
    : provenance.startsWith('practice')
      ? { label: 'practice-cited', paint: VERDICT.complying }
      : provenance.startsWith('dev-placeholder')
        ? { label: 'dev-placeholder', paint: ABSENCE }
        : { label: 'legacy', paint: INK.secondary };
  return (
    <StrataChip paint={tier.paint} title={provenance}>
      {tier.label}
    </StrataChip>
  );
}

/** Absence: NULL is a loud fact, never silence -- and it is NOT a
 * violation, so every absence wears the one amber (UNPRICED,
 * UNVERIFIED, NULL, PROXY...). */
export function AbsenceBadge({ label, reason }: { label: string; reason?: string }) {
  return (
    <StrataChip paint={ABSENCE} title={reason}>
      {label.toUpperCase()}
    </StrataChip>
  );
}

/** Approval: the standing human fact, always carrying WHO. */
export function ApprovalMark({
  decision,
  who,
}: {
  decision: 'verified' | 'accepted' | 'rejected' | 'ratified';
  who: string;
}) {
  const paint = decision === 'rejected' ? INK.muted : VERDICT.complying;
  return (
    <StrataChip paint={paint}>
      {decision} · {who}
    </StrataChip>
  );
}

/** The suggestion kinds the edit machinery can apply mechanically;
 * everything else is advisory -- informational until a human acts,
 * and NEVER offered the apply path. */
const APPLIABLE_KINDS = new Set(['lower_end_connected', 'translate_down']);
export const isAppliable = (kind: string) => APPLIABLE_KINDS.has(kind);

/** Suggestion: a derived proposal. Advisory kinds announce
 * themselves; appliable kinds let the accept button carry the verb. */
export function SuggestionNote({ kind, description }: { kind: string; description: string }) {
  return (
    <div style={{ fontSize: 12, color: ABSENCE, margin: '0.25rem 0' }}>
      {!isAppliable(kind) && (
        <span style={{ marginRight: 6 }}>
          <StrataChip paint={INK.secondary}>advisory</StrataChip>
        </span>
      )}
      {description}
    </div>
  );
}

/** Run: the compute's testimony as a rollup. The affirmative claim is
 * first-class -- complying counts render even when nothing violates. */
export function RunRollup({
  compliant,
  breach,
}: {
  compliant: number;
  breach: number;
}) {
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      <ComplianceTag state="complying">{compliant} complying</ComplianceTag>
      {breach > 0 && <ComplianceTag state="violating">{breach} violating</ComplianceTag>}
    </span>
  );
}

/** Element identity: the category dot. Same hue on the canvas, the
 * legend, the overview, and the review queue -- color follows the
 * entity, never the screen. */
export function CategorySwatch({ category, size = 10 }: { category: string; size?: number }) {
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'inline-block',
        backgroundColor: CATEGORY[category] ?? CATEGORY_DEFAULT,
      }}
    />
  );
}
